<script setup lang="ts">
import type Guid from "@/app/types/common/guid";
import type { DialogEvents } from "@/components/mixins/dialogs";
import { useReactiveDialogScriptSetup } from "@/components/mixins/dialogs";
import { onBeforeMount, ref, toRef } from "vue";
import messages, { keys } from "@/plugins/i18n";
import { withLoading } from "@/components/composables/componentTools";
import {
  PlannedSalesVisit,
  salesVisitService,
} from "@/app/services/generated/salesvisitService";
import api from "@/app/api";
import BmDialog from "@/components/dialogs/BmDialog.vue";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";

export interface CompleteVisitDialogProps {
  value: boolean;
  plannedSalesVisitId: Guid;
}

export interface CompleteVisitDialogEvents extends DialogEvents {}

const props = defineProps<CompleteVisitDialogProps>();
const emit = defineEmits<CompleteVisitDialogEvents>();

const { accept, cancel } = useReactiveDialogScriptSetup(
  toRef(props, "value"),
  emit
);

const plannedSalesVisit = ref<PlannedSalesVisit>(new PlannedSalesVisit());

onBeforeMount(
  withLoading(async () => {
    if (props.plannedSalesVisitId !== null) {
      const plannedVisit = await salesVisitService.getPlannedSalesVisit(
        api.tenantId,
        props.plannedSalesVisitId
      );
      const visitNextRepeatDate =
        await salesVisitService.getNextRepeatPlannedDate(
          api.tenantId,
          props.plannedSalesVisitId
        );

      plannedSalesVisit.value = plannedVisit;
      plannedSalesVisit.value.date = visitNextRepeatDate ?? tenantService.today;
    }
  })
);

const completePlannedVisit = withLoading(async () => {
  await salesVisitService.savePlannedSalesVisit(
    api.tenantId,
    plannedSalesVisit.value
  );
  accept();
});
</script>

<template>
  <bm-dialog
    :label="messages.sales_visit_create_next_plan_visit"
    :visible="value"
    @accept="completePlannedVisit"
    @cancel="cancel"
  >
    <v-container>
      <v-row>
        <v-col>
          <bm-date
            v-model="plannedSalesVisit.date"
            :t="keys.sales_visit_next_visit"
            :rules="[$ruleSet.required()]"
          />
          <bm-time
            v-model="plannedSalesVisit.plannedTime"
            :t="keys.sales_visit_planned_time"
          />
          <bm-switch
            v-model="plannedSalesVisit.isPlannedTimeFlexible"
            :t="keys.sales_visit_is_planned_start_time_flexible"
          />
          <bm-number
            v-model="plannedSalesVisit.durationMinutes"
            :t="keys.sales_visit_duration"
            :min="0"
            :rules="[$ruleSet.notNegativeNumber()]"
            :suffix="$m.units_mins_suffix"
          />
        </v-col>
      </v-row>
    </v-container>
    <template #controls-confirm-text>
      {{ messages.create }}
    </template>
  </bm-dialog>
</template>
